import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const PageContainer = styled.div`
  min-height: calc(100vh - 80px);
  padding: 4rem 2rem;
  margin-top: 80px;
  background: linear-gradient(135deg, #f8fafc 0%, #edf2f7 100%);
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 30%, rgba(147, 197, 253, 0.1) 0%, transparent 70%),
      radial-gradient(circle at 80% 70%, rgba(96, 165, 250, 0.1) 0%, transparent 70%);
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  position: relative;
  z-index: 1;
`;

const Title = styled(motion.h1)`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, #2563eb 0%, #3b82f6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  color: #4a5568;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.7;
`;

const SearchContainer = styled.div`
  max-width: 600px;
  margin: 3rem auto;
  position: relative;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 1.25rem 1.5rem;
  padding-left: 3rem;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: white;
  font-size: 1.1rem;
  color: #1e293b;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 4px 6px rgba(37, 99, 235, 0.1);
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
`;

const CategoriesContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin: 2rem auto 4rem;
  flex-wrap: wrap;
  max-width: 800px;
  padding: 0.5rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);

  @media (max-width: 768px) {
    gap: 0.5rem;
    padding: 0.75rem;
    margin: 2rem 1rem 4rem;
  }
`;

const FilterButton = styled(motion.button)`
  position: relative;
  background: ${props => props.isActive ? 'white' : 'transparent'};
  color: ${props => props.isActive ? '#2563eb' : '#64748b'};
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  border: none;
  font-size: 0.95rem;
  font-weight: ${props => props.isActive ? '600' : '500'};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  ${props => props.isActive && `
    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.1);
  `}

  &:hover {
    color: ${props => props.isActive ? '#2563eb' : '#2563eb'};
    background: ${props => props.isActive ? 'white' : 'rgba(37, 99, 235, 0.05)'};
  }

  .icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    font-size: 1rem;
  }

  .count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${props => props.isActive ? 'rgba(37, 99, 235, 0.1)' : 'rgba(100, 116, 139, 0.1)'};
    color: ${props => props.isActive ? '#2563eb' : '#64748b'};
    padding: 0.25rem 0.5rem;
    border-radius: 6px;
    font-size: 0.8rem;
    font-weight: 500;
    min-width: 24px;
  }

  @media (max-width: 768px) {
    padding: 0.6rem 1rem;
    font-size: 0.9rem;
  }
`;

const FAQContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const FAQSection = styled.div`
  margin-bottom: 4rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;

  svg {
    width: 24px;
    height: 24px;
    color: #2563eb;
  }
`;

const FAQItem = styled(motion.div)`
  background: white;
  border-radius: 12px;
  margin-bottom: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow: hidden;
`;

const QuestionButton = styled.button`
  width: 100%;
  padding: 1.5rem;
  text-align: left;
  background: none;
  border: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  color: #1e293b;
  font-weight: 600;
  font-size: 1.1rem;
  transition: all 0.3s ease;

  &:hover {
    color: #2563eb;
  }

  svg {
    width: 20px;
    height: 20px;
    color: #94a3b8;
    transition: transform 0.3s ease;
    transform: rotate(${props => props.isOpen ? '180deg' : '0deg'});
  }
`;

const Answer = styled(motion.div)`
  padding: 0 1.5rem 1.5rem;
  color: #64748b;
  line-height: 1.7;
  font-size: 1.05rem;
`;

const ContactSection = styled.div`
  text-align: center;
  margin-top: 6rem;
  padding: 3rem;
  background: white;
  border-radius: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
`;

const ContactTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1rem;
`;

const ContactText = styled.p`
  color: #64748b;
  margin-bottom: 2rem;
  font-size: 1.1rem;
  line-height: 1.7;
`;

const ContactButton = styled(motion.button)`
  background: linear-gradient(135deg, #2563eb 0%, #3b82f6 100%);
  color: white;
  padding: 1rem 2.5rem;
  border-radius: 50px;
  border: none;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(37, 99, 235, 0.1);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(37, 99, 235, 0.2);
  }
`;

function FAQ() {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [openQuestions, setOpenQuestions] = useState({});

  const filters = [
    { id: 'all', label: 'All Questions', icon: '🔍' },
    { id: 'general', label: 'General', icon: '📌' },
    { id: 'pricing', label: 'Pricing', icon: '💰' },
    { id: 'technical', label: 'Technical', icon: '⚙️' },
    { id: 'security', label: 'Security', icon: '🔒' },
    { id: 'support', label: 'Support', icon: '💬' }
  ];

  const faqData = {
    general: [
      {
        question: "What is Revguard?",
        answer: "Revguard is a comprehensive testing platform designed specifically for Shopify stores. We help prevent lost sales by continuously monitoring and testing your store's checkout process, performance, security, and integrations."
      },
      {
        question: "How does Revguard work?",
        answer: "Our platform runs automated tests on your store, checking everything from checkout functionality to mobile responsiveness. We monitor your store 24/7 and alert you immediately if we detect any issues that could impact your sales."
      },
      {
        question: "Who is Revguard for?",
        answer: "Revguard is perfect for Shopify store owners who want to ensure their store runs smoothly and prevent lost sales. Whether you're a growing store or an established enterprise, we have plans to suit your needs."
      }
    ],
    pricing: [
      {
        question: "What payment methods do you accept?",
        answer: "We accept all major credit cards, PayPal, and can arrange bank transfers for enterprise clients. All payments are processed securely through Stripe."
      },
      {
        question: "Can I change my plan later?",
        answer: "Yes, you can upgrade or downgrade your plan at any time. Changes will be reflected in your next billing cycle."
      },
      {
        question: "Do you offer refunds?",
        answer: "Yes, we offer a 14-day money-back guarantee. If you're not satisfied with our service, we'll refund your payment in full – no questions asked."
      }
    ],
    technical: [
      {
        question: "How do I integrate Revguard with my store?",
        answer: "Integration is simple and requires no coding. Just install our Shopify app and we'll handle the rest. For enterprise clients, we offer custom integration support."
      },
      {
        question: "Will Revguard slow down my store?",
        answer: "No, Revguard runs independently of your store and has zero impact on your store's performance. Our monitoring is completely external."
      },
      {
        question: "What types of tests do you run?",
        answer: "We run comprehensive tests including checkout flow, payment processing, mobile responsiveness, performance optimization, security vulnerabilities, and third-party integrations."
      }
    ],
    security: [
      {
        question: "Is my store data secure?",
        answer: "Yes, we take security seriously. All data is encrypted, and we're compliant with major security standards including PCI DSS and GDPR."
      },
      {
        question: "Do you store sensitive customer data?",
        answer: "No, we never store sensitive customer data. Our testing process uses dummy data and secure test environments."
      },
      {
        question: "What security certifications do you have?",
        answer: "We maintain ISO 27001 certification and undergo regular security audits. Our platform is built with enterprise-grade security measures."
      }
    ],
    support: [
      {
        question: "What kind of support do you offer?",
        answer: "We offer email support for all plans, with priority support and dedicated account managers for Scale and Enterprise plans. Enterprise clients also get 24/7 phone support."
      },
      {
        question: "How quickly do you respond to issues?",
        answer: "For critical issues, we respond within 1 hour. Regular support tickets are answered within 24 hours. Enterprise clients get immediate support."
      },
      {
        question: "Do you offer training?",
        answer: "Yes, we provide comprehensive documentation, video tutorials, and personalized onboarding. Enterprise clients receive custom training sessions."
      }
    ]
  };

  const toggleQuestion = (categoryId, index) => {
    setOpenQuestions(prev => ({
      ...prev,
      [`${categoryId}-${index}`]: !prev[`${categoryId}-${index}`]
    }));
  };

  const handleFilterClick = (filterId) => {
    setActiveFilter(filterId);
    setSearchQuery('');
    // Reset open questions when changing filters
    setOpenQuestions({});
  };

  const handleContactSupport = () => {
    navigate('/contact');
  };

  const getQuestionCount = (filterId) => {
    if (filterId === 'all') {
      return Object.values(faqData).reduce((total, questions) => total + questions.length, 0);
    }
    return faqData[filterId]?.length || 0;
  };

  const filteredFAQs = Object.entries(faqData).reduce((acc, [category, questions]) => {
    if (searchQuery) {
      const matchingQuestions = questions.filter(q => 
        q.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        q.answer.toLowerCase().includes(searchQuery.toLowerCase())
      );
      if (matchingQuestions.length > 0) {
        acc[category] = matchingQuestions;
      }
    } else if (activeFilter === 'all' || activeFilter === category) {
      acc[category] = questions;
    }
    return acc;
  }, {});

  return (
    <PageContainer>
      <Header>
        <Title
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          How can we help?
        </Title>
        <Subtitle>
          Find answers to common questions about our platform and services
        </Subtitle>
        <SearchContainer>
          <SearchIcon>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </SearchIcon>
          <SearchInput
            type="text"
            placeholder="Search for answers..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setActiveFilter('all');
            }}
          />
        </SearchContainer>
      </Header>

      <CategoriesContainer>
        {filters.map((filter) => (
          <FilterButton
            key={filter.id}
            isActive={activeFilter === filter.id}
            onClick={() => handleFilterClick(filter.id)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <span className="icon">{filter.icon}</span>
            {filter.label}
            <span className="count">{getQuestionCount(filter.id)}</span>
          </FilterButton>
        ))}
      </CategoriesContainer>

      <FAQContainer>
        <AnimatePresence mode="wait">
          {Object.entries(filteredFAQs).map(([category, questions]) => (
            <FAQSection key={category}>
              <SectionTitle>
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </SectionTitle>
              {questions.map((faq, index) => (
                <FAQItem
                  key={`${category}-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <QuestionButton
                    onClick={() => toggleQuestion(category, index)}
                    isOpen={openQuestions[`${category}-${index}`]}
                  >
                    {faq.question}
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </QuestionButton>
                  <AnimatePresence>
                    {openQuestions[`${category}-${index}`] && (
                      <Answer
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        {faq.answer}
                      </Answer>
                    )}
                  </AnimatePresence>
                </FAQItem>
              ))}
            </FAQSection>
          ))}
        </AnimatePresence>
      </FAQContainer>

      <ContactSection>
        <ContactTitle>Still have questions?</ContactTitle>
        <ContactText>
          Can't find the answer you're looking for? Our team is here to help.
        </ContactText>
        <ContactButton
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleContactSupport}
        >
          Contact Support
        </ContactButton>
      </ContactSection>
    </PageContainer>
  );
}

export default FAQ;